import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import {
    BarChart3, Cpu, Layout, Layers, Clock, AlertTriangle, Plus,
    TrendingUp, CheckCircle2, Loader2, RefreshCw, ChevronRight, Calendar,
    Target, Zap, ArrowRight, Info, HelpCircle, Users, BookOpen, Star, Sparkles
} from 'lucide-react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { ScrollArea } from '../ui/scroll-area';
import { Tooltip } from '../ui/tooltip';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import api from '../../api';
import PipelineWizard from '../pipeline/PipelineWizard';
import TemplateDialog from '../pipeline/TemplateDialog';

// ─── Faculty Color Hashing ───────────────────────────────
// Deterministic color per faculty_id for instant visual scanning
const FACULTY_PALETTE = [
    '#3b82f6', '#ef4444', '#10b981', '#f59e0b', '#8b5cf6',
    '#ec4899', '#06b6d4', '#f97316', '#14b8a6', '#6366f1',
    '#e11d48', '#0ea5e9', '#84cc16', '#d946ef', '#78716c',
];

function getFacultyColor(facultyId) {
    if (!facultyId) return '#6b7280';
    let hash = 0;
    for (let i = 0; i < facultyId.length; i++) {
        hash = ((hash << 5) - hash) + facultyId.charCodeAt(i);
        hash |= 0;
    }
    return FACULTY_PALETTE[Math.abs(hash) % FACULTY_PALETTE.length];
}

// ─── Metric Tooltip Descriptions ─────────────────────────
const METRIC_TOOLTIPS = {
    assignments: "Total faculty-subject-batch assignments requiring scheduling. Each assignment represents a recurring teaching slot.",
    scheduled: "Number of lecture events placed on the main calendar. Increases as you publish pipeline results.",
    tentative: "Events in draft blueprints not yet published. These are plans waiting for admin approval.",
    coverage: "Average scheduling progress across all assignments. Calculated as (scheduled hours ÷ required hours) per assignment, averaged. 100% = fully scheduled.",
    bottleneck: "Faculty whose total assigned weekly hours exceed 80% of their weekly capacity (max_hours_per_day × 5 days). These instructors may need load redistribution.",
};

// ─── Metric Card ─────────────────────────────────────────
function MetricCard({ icon: Icon, label, value, unit = '', color = 'text-blue-500', tooltip }) {
    return (
        <Tooltip content={tooltip} side="bottom">
            <div className="data-card p-3.5 flex items-start gap-3 group cursor-help hover:border-zinc-300 dark:hover:border-zinc-600 transition-colors">
                <div className={`p-2 rounded-lg bg-zinc-50 dark:bg-zinc-800 ${color}`}>
                    <Icon className="w-4 h-4" />
                </div>
                <div className="flex-1 min-w-0">
                    <div className="text-[10px] uppercase tracking-wider text-zinc-400 font-medium flex items-center gap-1">
                        {label}
                        {tooltip && <HelpCircle className="w-3 h-3 opacity-0 group-hover:opacity-50 transition-opacity" />}
                    </div>
                    <div className="text-xl font-bold text-zinc-900 dark:text-zinc-100 mt-0.5 tabular-nums">
                        {value}{unit && <span className="text-sm font-medium text-zinc-400 ml-0.5">{unit}</span>}
                    </div>
                </div>
            </div>
        </Tooltip>
    );
}

// ─── Template Chip ───────────────────────────────────────
function TemplateChip({ template, onSelect }) {
    return (
        <button
            onClick={onSelect}
            className="data-card px-3 py-2 flex-shrink-0 hover:border-blue-400 transition-all group cursor-pointer"
        >
            <div className="flex items-center gap-2 mb-1">
                <div className="w-2 h-2 rounded-full bg-blue-500" />
                <span className="text-xs font-semibold text-zinc-700 dark:text-zinc-300 group-hover:text-blue-500 transition-colors flex items-center gap-1.5">
                    {template.name}
                    {template.is_starred && <Star className="w-3 h-3 text-amber-500 fill-current" />}
                </span>
            </div>
            <div className="text-[10px] text-zinc-400">
                {template.event_count || 0} events
                {template.is_active && <Badge variant="secondary" className="ml-1.5 text-[9px] px-1 py-0 h-3.5">Active</Badge>}
            </div>
        </button>
    );
}

// ─── Pipeline Action Card ────────────────────────────────
function PipelineCard({ icon: Icon, title, desc, color, bgGlow, status, onClick }) {
    return (
        <button
            onClick={onClick}
            className="data-card p-4 text-left group hover:border-zinc-300 dark:hover:border-zinc-600 transition-all relative overflow-hidden"
        >
            <div className={`absolute inset-0 opacity-0 group-hover:opacity-5 transition-opacity ${bgGlow}`} />
            <div className="relative">
                <div className="flex items-center justify-between mb-2">
                    <div className={`p-2 rounded-lg bg-zinc-50 dark:bg-zinc-800 ${color}`}>
                        <Icon className="w-4 h-4" />
                    </div>
                    <Badge variant="outline" className="text-[10px]">{status}</Badge>
                </div>
                <h3 className="text-sm font-semibold text-zinc-700 dark:text-zinc-200">{title}</h3>
                <p className="text-xs text-zinc-400 mt-0.5">{desc}</p>
                <div className="flex items-center justify-end mt-3">
                    <span className="text-xs text-zinc-400 flex items-center gap-0.5 group-hover:text-blue-500 transition-colors">
                        Start <ChevronRight className="w-3 h-3" />
                    </span>
                </div>
            </div>
        </button>
    );
}

// ─── Bottleneck Drill-Down Dialog ────────────────────────
function BottleneckDialog({ open, onOpenChange, faculty }) {
    if (!faculty) return null;
    const color = getFacultyColor(faculty.faculty_id);
    const utilizationColor = faculty.utilization_pct > 95 ? 'text-red-500' : faculty.utilization_pct > 80 ? 'text-amber-500' : 'text-emerald-500';

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-md" aria-describedby="bottleneck-dialog-description">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <div className="w-3 h-3 rounded-full" style={{ backgroundColor: color }} />
                        {faculty.name}
                    </DialogTitle>
                    <DialogDescription id="bottleneck-dialog-description">
                        Faculty utilization analysis
                    </DialogDescription>
                </DialogHeader>

                <div className="space-y-4">
                    {/* Utilization gauge */}
                    <div className="data-card p-4">
                        <div className="flex items-center justify-between mb-2">
                            <span className="text-xs text-zinc-500">Weekly Utilization</span>
                            <span className={`text-lg font-bold tabular-nums ${utilizationColor}`}>
                                {faculty.utilization_pct}%
                            </span>
                        </div>
                        <div className="h-3 w-full bg-zinc-200 dark:bg-zinc-700 rounded-full overflow-hidden">
                            <div
                                className={`h-full rounded-full transition-all duration-700 ${faculty.utilization_pct > 95 ? 'bg-red-500' :
                                    faculty.utilization_pct > 80 ? 'bg-amber-500' : 'bg-emerald-500'
                                    }`}
                                style={{ width: `${Math.min(100, faculty.utilization_pct)}%` }}
                            />
                        </div>
                    </div>

                    {/* Load breakdown */}
                    <div className="grid grid-cols-2 gap-3">
                        <div className="data-card p-3 text-center">
                            <div className="text-lg font-bold text-zinc-900 dark:text-zinc-100">
                                {faculty.assigned_hours_per_week || faculty.event_count || '?'}
                            </div>
                            <div className="text-[10px] text-zinc-400 uppercase">Assigned hrs/wk</div>
                        </div>
                        <div className="data-card p-3 text-center">
                            <div className="text-lg font-bold text-zinc-900 dark:text-zinc-100">
                                {faculty.max_weekly_hours || faculty.max_hours || '?'}
                            </div>
                            <div className="text-[10px] text-zinc-400 uppercase">Max capacity/wk</div>
                        </div>
                    </div>

                    {/* Recommendation */}
                    <div className="flex items-start gap-2 p-3 rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800">
                        <AlertTriangle className="w-4 h-4 text-amber-600 dark:text-amber-400 mt-0.5 flex-shrink-0" />
                        <div className="text-xs text-amber-700 dark:text-amber-300">
                            <strong>Recommendation:</strong> Consider redistributing some of this faculty's assignments to reduce over-utilization.
                            {faculty.utilization_pct > 95
                                ? " Critical — schedule conflicts are very likely."
                                : " Approaching capacity — minor conflicts possible."}
                        </div>
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
}

// ═══════════════════════════════════════════════════════════
// MAIN COMPONENT
// ═══════════════════════════════════════════════════════════
export default function SchedulingPage() {
    const [metrics, setMetrics] = useState(null);
    const [templates, setTemplates] = useState([]);
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [activePipeline, setActivePipeline] = useState(null); // { type, id }
    const [selectedBottleneck, setSelectedBottleneck] = useState(null);
    const [templateDialogOpen, setTemplateDialogOpen] = useState(false);
    const [selectedTemplateId, setSelectedTemplateId] = useState(null);
    const [sourceDate, setSourceDate] = useState('');

    const fetchDashboard = useCallback(async (silent = false) => {
        try {
            if (!silent) setLoading(true);
            else setRefreshing(true);

            const [metricsRes, templatesRes] = await Promise.all([
                api.get('/api/scheduling/dashboard'),
                api.get('/api/templates', { params: { session_id: 'none' } }),
            ]);

            setMetrics(metricsRes.data);
            // Sort templates: starred first, then by name
            const sortedTemplates = (templatesRes.data || []).sort((a, b) => {
                if (a.is_starred && !b.is_starred) return -1;
                if (!a.is_starred && b.is_starred) return 1;
                return (a.name || '').localeCompare(b.name || '');
            });
            setTemplates(sortedTemplates);
        } catch (err) {
            console.error('Dashboard fetch error:', err);
            if (!silent) toast.error('Failed to load dashboard');
        } finally {
            setLoading(false);
            setRefreshing(false);
        }
    }, []);

    useEffect(() => { fetchDashboard(); }, [fetchDashboard]);

    const handlePipelineClick = (pipelineType, sessionId = null) => {
        setActivePipeline({ type: pipelineType, id: sessionId });
    };

    const abortPipeline = async (sessionId) => {
        try {
            setRefreshing(true);
            await api.delete(`/api/pipeline/${sessionId}`);
            toast.success("Pipeline aborted successfully.");
            fetchDashboard(true);
        } catch (err) {
            console.error("Failed to abort pipeline:", err);
            toast.error("Failed to abort pipeline.");
            setRefreshing(false);
        }
    };

    if (activePipeline) {
        return (
            <div className="flex-1 flex flex-col">
                <PipelineWizard
                    pipelineType={activePipeline.type}
                    existingSessionId={activePipeline.id}
                    onClose={() => { setActivePipeline(null); fetchDashboard(true); }}
                />
            </div>
        );
    }

    if (loading) {
        return (
            <div className="flex-1 flex items-center justify-center">
                <div className="flex flex-col items-center gap-3">
                    <Loader2 className="w-6 h-6 animate-spin text-blue-500" />
                    <span className="text-sm text-zinc-400">Loading scheduling dashboard...</span>
                </div>
            </div>
        );
    }

    return (
        <ScrollArea className="flex-1">
            <div className="max-w-7xl mx-auto p-6 space-y-6">
                {/* Header */}
                <div className="flex items-center justify-between">
                    <div>
                        <h1 className="text-xl font-bold text-zinc-900 dark:text-zinc-100 flex items-center gap-2">
                            <Sparkles className="w-5 h-5 text-blue-500" />
                            Algorithm Creator
                        </h1>
                        <p className="text-sm text-zinc-400 mt-0.5">Directly generate templates and manage scheduling pipelines</p>
                    </div>
                    <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => fetchDashboard(true)}
                        disabled={refreshing}
                        className="text-zinc-400 hover:text-zinc-600"
                    >
                        <RefreshCw className={`w-4 h-4 mr-1.5 ${refreshing ? 'animate-spin' : ''}`} />
                        Refresh
                    </Button>
                </div>

                {/* ─── Metrics Grid ─────────────────────────────── */}
                {metrics && (
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                        <MetricCard
                            icon={Target}
                            label="Assignments"
                            value={metrics.total_assignments}
                            color="text-blue-500"
                            tooltip={METRIC_TOOLTIPS.assignments}
                        />
                        <MetricCard
                            icon={CheckCircle2}
                            label="Scheduled"
                            value={metrics.scheduled_events}
                            color="text-emerald-500"
                            tooltip={METRIC_TOOLTIPS.scheduled}
                        />
                        <MetricCard
                            icon={Clock}
                            label="Tentative"
                            value={metrics.tentative_events}
                            color="text-amber-500"
                            tooltip={METRIC_TOOLTIPS.tentative}
                        />
                        <MetricCard
                            icon={BarChart3}
                            label="Coverage"
                            value={metrics.coverage_pct}
                            unit="%"
                            color="text-violet-500"
                            tooltip={METRIC_TOOLTIPS.coverage}
                        />
                    </div>
                )}

                {/* ─── Status Row ───────────────────────────────── */}
                {metrics && (
                    <div className="flex items-center gap-4 text-xs text-zinc-400 flex-wrap">
                        <div className="flex items-center gap-1.5">
                            <Layout className="w-3.5 h-3.5" />
                            <span><strong className="text-zinc-600 dark:text-zinc-300">{metrics.active_templates}</strong> active templates</span>
                        </div>
                        <div className="flex items-center gap-1.5">
                            <Layers className="w-3.5 h-3.5" />
                            <span><strong className="text-zinc-600 dark:text-zinc-300">{metrics.living_blueprints}</strong> blueprints</span>
                        </div>
                        {metrics.bottleneck_faculty?.length > 0 && (
                            <Tooltip content={METRIC_TOOLTIPS.bottleneck} side="bottom">
                                <div className="flex items-center gap-1.5 text-amber-500 cursor-help">
                                    <AlertTriangle className="w-3.5 h-3.5" />
                                    <span><strong>{metrics.bottleneck_faculty.length}</strong> bottleneck faculty</span>
                                    <HelpCircle className="w-3 h-3 opacity-50" />
                                </div>
                            </Tooltip>
                        )}
                    </div>
                )}

                {/* ─── Direct Template Creation (New) ─────────────────── */}
                <div className="data-card p-5 bg-gradient-to-br from-blue-500/5 to-fuchsia-500/5 border-blue-500/10">
                    <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                        <div className="space-y-1">
                            <h2 className="text-sm font-bold text-zinc-800 dark:text-zinc-100 flex items-center gap-2">
                                <Zap className="w-4 h-4 text-amber-500" />
                                Instant Template Discovery
                            </h2>
                            <p className="text-xs text-zinc-500">Pick a day with an ideal schedule to convert it into a reusable template instantly.</p>
                        </div>
                        <div className="flex items-center gap-2">
                            <input 
                                type="date" 
                                value={sourceDate}
                                onChange={e => setSourceDate(e.target.value)}
                                className="h-9 px-3 rounded-md border border-zinc-200 dark:border-zinc-700 bg-white dark:bg-zinc-800 text-xs focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 outline-none transition-all"
                            />
                            <Button 
                                size="sm" 
                                className="bg-blue-600 hover:bg-blue-700 shadow-sm"
                                onClick={async () => {
                                    if (!sourceDate) {
                                        toast.error("Please select a source date first");
                                        return;
                                    }
                                    const loadingToast = toast.loading(`Discovering template from ${sourceDate}...`);
                                    try {
                                        const res = await api.post('/api/templates/from-day', { date: sourceDate });
                                        toast.success(`Template "${res.data.name}" created!`, { id: loadingToast });
                                        fetchDashboard(true);
                                        setSourceDate('');
                                    } catch (err) {
                                        toast.error(err?.response?.data?.detail || "Failed to create template", { id: loadingToast });
                                    }
                                }}
                            >
                                <Plus className="w-4 h-4 mr-2" />
                                Generate Template
                            </Button>
                        </div>
                    </div>
                </div>

                {/* ─── Quick-Access Templates ───────────────────── */}
                <div>
                    <div className="flex items-center justify-between mb-2">
                        <h2 className="text-sm font-semibold text-zinc-700 dark:text-zinc-300 flex items-center gap-1.5">
                            <Layout className="w-4 h-4 text-zinc-400" />
                            Active Templates
                        </h2>
                        <Button variant="ghost" size="sm" className="text-xs text-zinc-400 h-7"
                            onClick={() => { setSelectedTemplateId(null); setTemplateDialogOpen(true); }}>
                            <Plus className="w-3 h-3 mr-1" />
                            New Template
                        </Button>
                    </div>
                    {templates.length > 0 ? (
                        <div className="flex gap-2 overflow-x-auto pb-1 scrollbar-thin">
                            {templates.map(t => (
                                <TemplateChip key={t.id} template={t}
                                    onSelect={() => { setSelectedTemplateId(t.id); setTemplateDialogOpen(true); }} />
                            ))}
                        </div>
                    ) : (
                        <div className="text-xs text-zinc-400 py-4 text-center border border-dashed border-zinc-200 dark:border-zinc-700 rounded-lg">
                            No templates yet. Create one or use auto-discovery in a pipeline.
                        </div>
                    )}
                </div>

                {/* ─── Scheduling Pipelines ─────────────────────── */}
                <div>
                    <h2 className="text-sm font-semibold text-zinc-700 dark:text-zinc-300 mb-3 flex items-center gap-1.5">
                        <ArrowRight className="w-4 h-4 text-zinc-400" />
                        Scheduling Pipelines
                    </h2>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                        <PipelineCard
                            icon={Calendar}
                            title="Tentative Blueprint"
                            desc="Long-horizon planning & feasibility"
                            color="text-blue-500"
                            bgGlow="bg-blue-500"
                            status="Ready"
                            onClick={() => handlePipelineClick('blueprint')}
                        />
                        <PipelineCard
                            icon={Clock}
                            title="Fixed Day Scheduling"
                            desc="Generate daily schedules from templates"
                            color="text-emerald-500"
                            bgGlow="bg-emerald-500"
                            status="Ready"
                            onClick={() => handlePipelineClick('fixed_day')}
                        />
                        <PipelineCard
                            icon={RefreshCw}
                            title="Post-Schedule Updates"
                            desc="Swaps, reschedules & patch changes"
                            color="text-violet-500"
                            bgGlow="bg-violet-500"
                            status="Ready"
                            onClick={() => handlePipelineClick('post_update')}
                        />
                    </div>
                </div>



                {/* ─── Bottleneck Alerts (Enhanced) ────────────────── */}
                {metrics?.bottleneck_faculty?.length > 0 && (
                    <div className="data-card p-4 mb-6">
                        <h3 className="text-xs font-semibold text-amber-600 dark:text-amber-400 uppercase mb-3 flex items-center gap-1.5">
                            <AlertTriangle className="w-3.5 h-3.5" />
                            Faculty Bottlenecks
                            <Tooltip content={METRIC_TOOLTIPS.bottleneck} side="right">
                                <HelpCircle className="w-3 h-3 opacity-50 cursor-help" />
                            </Tooltip>
                        </h3>
                        <div className="space-y-2">
                            {metrics.bottleneck_faculty.map(f => {
                                const color = getFacultyColor(f.faculty_id);
                                return (
                                    <button
                                        key={f.faculty_id}
                                        onClick={() => setSelectedBottleneck(f)}
                                        className="w-full flex items-center justify-between text-sm p-2 rounded-lg hover:bg-zinc-50 dark:hover:bg-zinc-800 transition-colors group cursor-pointer"
                                    >
                                        <div className="flex items-center gap-2">
                                            <div className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: color }} />
                                            <span className="text-zinc-600 dark:text-zinc-300 group-hover:text-zinc-900 dark:group-hover:text-zinc-100 transition-colors">
                                                {f.name}
                                            </span>
                                        </div>
                                        <div className="flex items-center gap-2">
                                            <div className="h-1.5 w-24 bg-zinc-200 dark:bg-zinc-700 rounded-full overflow-hidden">
                                                <div
                                                    className={`h-full rounded-full transition-all ${f.utilization_pct > 95 ? 'bg-red-500' : 'bg-amber-500'
                                                        }`}
                                                    style={{ width: `${Math.min(100, f.utilization_pct)}%` }}
                                                />
                                            </div>
                                            <span className="text-xs text-zinc-400 tabular-nums w-10 text-right">{f.utilization_pct}%</span>
                                            <ChevronRight className="w-3.5 h-3.5 text-zinc-300 group-hover:text-zinc-500 transition-colors" />
                                        </div>
                                    </button>
                                );
                            })}
                        </div>
                    </div>
                )}

                {/* No bottlenecks — positive signal */}
                {metrics && (!metrics.bottleneck_faculty || metrics.bottleneck_faculty.length === 0) && (
                    <div className="data-card p-4 border-emerald-200 dark:border-emerald-800">
                        <div className="flex items-center gap-2 text-sm text-emerald-600 dark:text-emerald-400">
                            <CheckCircle2 className="w-4 h-4" />
                            <span className="font-medium">No faculty bottlenecks detected</span>
                        </div>
                        <p className="text-xs text-zinc-400 mt-1 ml-6">
                            All faculty are within 80% of their weekly scheduling capacity.
                        </p>
                    </div>
                )}
            </div>

            {/* Bottleneck Drill-Down Dialog */}
            <BottleneckDialog
                open={!!selectedBottleneck}
                onOpenChange={(open) => !open && setSelectedBottleneck(null)}
                faculty={selectedBottleneck}
            />

            {/* Template View/Edit/Create Dialog */}
            <TemplateDialog
                open={templateDialogOpen}
                onClose={() => setTemplateDialogOpen(false)}
                templateId={selectedTemplateId}
                onSaved={() => fetchDashboard(true)}
            />
        </ScrollArea>
    );
}

// ─── Exported helper for use by event cards elsewhere ────
export { getFacultyColor, FACULTY_PALETTE };
